import React from 'react'

import { Button, ButtonVariant } from '@amzn/stencil-react-components/button'
import { Expander } from '@amzn/stencil-react-components/expander'
import { Input, InputWrapper } from '@amzn/stencil-react-components/input'
import { Col, Container, Spacer } from '@amzn/stencil-react-components/layout'
import { PopupDatePicker } from '@amzn/stencil-react-components/popup-date-picker'
import { Select } from '@amzn/stencil-react-components/select'
import { IconSearchSmall as IconSearch } from '@amzn/stencil-react-icons'

import { ResponsiveRow } from 'src/components/ResponsiveRow'
import { ModuleGroupStatus, ModuleGroupType } from 'src/models/dto/module-groups/ModuleGroupTypeDTO'

interface ModuleGroupsFilterProps {
    moduleGroupName: string
    typeOfEquivalency: ModuleGroupType[]
    moduleVersionId: string
    status: ModuleGroupStatus[]
    startDate: string
    endDate: string
    ownerOfRecord: string
    updateGroupName: (groupName: string) => void
    updateTypeOfEquivalency: (typeOfEquivalency: ModuleGroupType[]) => void
    updateModuleVersionId: (moduleVersionId: string) => void
    updateStatus: (status: ModuleGroupStatus[]) => void
    updateStartDate: (startDate: string) => void
    updateEndDate: (endDate: string) => void
    updateOwnerOfRecord: (ownerOfRecord: string) => void
    applyFilters: () => void
    resetFilters: () => void
    sideFilter: boolean
}

export const ModuleGroupsFilter = (props: ModuleGroupsFilterProps) => {
    const isStartDateDisabled = (date: string): boolean => {
        return !!props.endDate && new Date(date) > new Date(props.endDate)
    }

    const isEndDateDisabled = (date: string) => {
        return !!props.startDate && new Date(date) < new Date(props.startDate)
    }

    const isStartAfterEnd = () => {
        return (
            !!props.endDate &&
            !!props.startDate &&
            new Date(props.startDate) > new Date(props.endDate)
        )
    }

    if (props.sideFilter) {
        return (
            <Col gridGap='S200'>
                <InputWrapper id='module-group-name-input' labelText='Module group name'>
                    {(inputProps) => (
                        <Input
                            {...inputProps}
                            value={props.moduleGroupName}
                            insetElementLeading={{
                                element: <IconSearch aria-hidden={true} />,
                            }}
                            dataTestId='module-group-name-input'
                            placeholder='Enter module group'
                            aria-label='Module Group name'
                            onChange={(e) => {
                                props.updateGroupName(e.target.value)
                            }}
                        />
                    )}
                </InputWrapper>
                <InputWrapper id='module-version-id-input' labelText='Module Version ID'>
                    {(inputProps) => (
                        <Input
                            {...inputProps}
                            value={props.moduleVersionId}
                            dataTestId='module-version-id-input'
                            placeholder='Enter a Module Version ID'
                            aria-label='Module version id'
                            onChange={(e) => {
                                props.updateModuleVersionId(e.target.value)
                            }}
                        />
                    )}
                </InputWrapper>
                <InputWrapper id='type-of-equivalency-input' labelText='Type of equivalency'>
                    {(inputProps) => (
                        <Select
                            {...inputProps}
                            value={props.typeOfEquivalency}
                            dataTestId='type-of-equivalency-input'
                            placeholder='Select an equivalency'
                            aria-label='Type of equivalency'
                            options={Object.keys(ModuleGroupType)}
                            onChange={props.updateTypeOfEquivalency}
                            isMulti={true}
                        />
                    )}
                </InputWrapper>
                <InputWrapper id='status-input' labelText='Status'>
                    {(inputProps) => (
                        <Select
                            {...inputProps}
                            value={props.status}
                            dataTestId='status-input'
                            placeholder='Select a Status'
                            aria-label='Status'
                            options={Object.values(ModuleGroupStatus)}
                            onChange={props.updateStatus}
                            isMulti={true}
                        />
                    )}
                </InputWrapper>
                <InputWrapper id='owner-of-record-input' labelText='Owner of record'>
                    {(inputProps) => (
                        <Input
                            {...inputProps}
                            value={props.ownerOfRecord}
                            dataTestId='owner-of-record-input'
                            placeholder='Enter owner of record'
                            aria-label='Owner of record'
                            onChange={(e) => {
                                props.updateOwnerOfRecord(e.target.value)
                            }}
                        />
                    )}
                </InputWrapper>
                <InputWrapper
                    labelText='Start Date'
                    id='start-date-wrapper'
                    error={isStartAfterEnd()}
                    footer={isStartAfterEnd() ? 'Start date must be before end date' : undefined}
                >
                    {(dateProps) => (
                        <PopupDatePicker
                            {...dateProps}
                            id='start-date-input'
                            dataTestId='start-date-input'
                            inputProps={{
                                width: '100%',
                            }}
                            value={props.startDate}
                            onChange={props.updateStartDate}
                            isDateDisabled={(date) => isStartDateDisabled(date)}
                        />
                    )}
                </InputWrapper>
                <InputWrapper
                    labelText='End Date'
                    id='end-date-wrapper'
                    error={isStartAfterEnd()}
                    footer={isStartAfterEnd() ? 'Start date must be before end date' : undefined}
                >
                    {(dateProps) => (
                        <PopupDatePicker
                            {...dateProps}
                            id='end-date-input'
                            dataTestId='end-date-input'
                            inputProps={{ width: '100%' }}
                            value={props.endDate}
                            onChange={props.updateEndDate}
                            isDateDisabled={(date) => isEndDateDisabled(date)}
                        />
                    )}
                </InputWrapper>
                <Spacer height='S200' />
                <Button onClick={props.applyFilters} dataTestId='apply-module-group-filters'>
                    Filter
                </Button>
                <Button variant={ButtonVariant.Tertiary} onClick={props.resetFilters}>
                    Reset filter
                </Button>
            </Col>
        )
    }

    return (
        <Container backgroundColor={'neutral05'} paddingTop='S200' paddingHorizontal='S200'>
            <Expander titleText={'Filters'} shouldExpandOnMount={true}>
                <Container paddingHorizontal={'S600'} paddingBottom={'S200'}>
                    <ResponsiveRow gridGap={'S200'} padding={{ bottom: 'S200' }}>
                        <Col flex={1}>
                            <InputWrapper
                                id='module-group-name-input'
                                labelText='Module group name'
                            >
                                {(inputProps) => (
                                    <Input
                                        {...inputProps}
                                        value={props.moduleGroupName}
                                        insetElementLeading={{
                                            element: <IconSearch aria-hidden={true} />,
                                        }}
                                        dataTestId='module-group-name-input'
                                        placeholder='Enter module group'
                                        aria-label='Module Group name'
                                        onChange={(e) => {
                                            props.updateGroupName(e.target.value)
                                        }}
                                    />
                                )}
                            </InputWrapper>
                        </Col>
                        <Col flex={1}>
                            <InputWrapper
                                id='module-version-id-input'
                                labelText='Module Version ID'
                            >
                                {(inputProps) => (
                                    <Input
                                        {...inputProps}
                                        value={props.moduleVersionId}
                                        dataTestId='module-version-id-input'
                                        placeholder='Enter a Module Version ID'
                                        aria-label='Module version id'
                                        onChange={(e) => {
                                            props.updateModuleVersionId(e.target.value)
                                        }}
                                    />
                                )}
                            </InputWrapper>
                        </Col>
                    </ResponsiveRow>
                    <ResponsiveRow gridGap={'S200'} padding={{ bottom: 'S200' }}>
                        <Col flex={1}>
                            <InputWrapper
                                id='type-of-equivalency-input'
                                labelText='Type of equivalency'
                            >
                                {(inputProps) => (
                                    <Select
                                        {...inputProps}
                                        value={props.typeOfEquivalency}
                                        dataTestId='type-of-equivalency-input'
                                        placeholder='Select an equivalency'
                                        aria-label='Type of equivalency'
                                        options={Object.keys(ModuleGroupType)}
                                        onChange={props.updateTypeOfEquivalency}
                                        isMulti={true}
                                    />
                                )}
                            </InputWrapper>
                        </Col>
                        <Col flex={1}></Col>
                    </ResponsiveRow>
                    <ResponsiveRow gridGap={'S200'} padding={{ bottom: 'S200' }}>
                        <Col flex={1}>
                            <InputWrapper id='status-input' labelText='Status'>
                                {(inputProps) => (
                                    <Select
                                        {...inputProps}
                                        value={props.status}
                                        dataTestId='status-input'
                                        placeholder='Select a Status'
                                        aria-label='Status'
                                        options={Object.values(ModuleGroupStatus)}
                                        onChange={props.updateStatus}
                                        isMulti={true}
                                    />
                                )}
                            </InputWrapper>
                        </Col>
                        <Col flex={1}>
                            <InputWrapper id='owner-of-record-input' labelText='Owner of record'>
                                {(inputProps) => (
                                    <Input
                                        {...inputProps}
                                        value={props.ownerOfRecord}
                                        dataTestId='owner-of-record-input'
                                        placeholder='Enter owner of record'
                                        aria-label='Owner of record'
                                        onChange={(e) => {
                                            props.updateOwnerOfRecord(e.target.value)
                                        }}
                                    />
                                )}
                            </InputWrapper>
                        </Col>
                    </ResponsiveRow>
                    <ResponsiveRow gridGap={'S200'} padding={{ bottom: 'S200' }}>
                        <Col flex={1}>
                            <ResponsiveRow gridGap={'S200'} id={'row-for-time-frame'}>
                                <Col flex={1}>
                                    <PopupDatePicker
                                        id='start-date-input'
                                        dataTestId='start-date-input'
                                        labelText={'Start Date'}
                                        inputProps={{
                                            width: '100%',
                                        }}
                                        value={props.startDate}
                                        onChange={props.updateStartDate}
                                    />
                                </Col>
                                <Col flex={1}>
                                    <PopupDatePicker
                                        id='end-date-input'
                                        dataTestId='end-date-input'
                                        labelText={'End Date'}
                                        inputProps={{ width: '100%' }}
                                        value={props.endDate}
                                        onChange={props.updateEndDate}
                                    />
                                </Col>
                            </ResponsiveRow>
                        </Col>
                        <Col flex={1}></Col>
                    </ResponsiveRow>
                    <ResponsiveRow gridGap={'S200'} padding={{ top: 'S400', bottom: 'S200' }}>
                        <Button
                            onClick={props.applyFilters}
                            dataTestId='apply-module-group-filters'
                        >
                            Filter
                        </Button>
                        <Button variant={ButtonVariant.Tertiary} onClick={props.resetFilters}>
                            Reset filter
                        </Button>
                    </ResponsiveRow>
                </Container>
            </Expander>
        </Container>
    )
}
